// Copyright (c) Docugami, Inc. All rights reserved.

import { Event, IEvent } from "utils/Event";
import { Component, ComponentId } from "./Component";
import { Entity, EntityManager } from "./Entity";

/**
 * Defines a single query for entities with the given set of component IDs.
 * The query is kept up to date throughout the lifetime of the application.
 */
export class EntityQuery {
    private _components: ComponentId[] = [];
    private readonly _entityAddedEvent: Event<Entity> = new Event();
    private readonly _entityRemovedEvent: Event<Entity> = new Event();

    readonly key: string;

    entities: Entity[] = [];
    any(): boolean { return this.entities.length > 0 }
    single(): Entity | null {
        if (this.entities.length > 1) {
            throw new Error("Expected a single entity, but found more.");
        }

        return this.entities[0] ?? null;
    }

    first(): Entity | null { return this.entities[0] ?? null }
    last(): Entity | null { return this.entities[this.entities.length - 1] ?? null }
    get entityAdded(): IEvent<Entity> { return this._entityAddedEvent; }
    get entityRemoved(): IEvent<Entity> { return this._entityRemovedEvent; }

    constructor(types: ComponentId[], key: string) {
        this._components = types;
        this.key = key;
    }

    check(entity: Entity) {
        for (const type of this._components) {
            if (!entity.hasComponent(type)) {
                return false;
            }
        }

        return true;
    }

    hasComponent(type: ComponentId) {
        return this._components.includes(type);
    }

    addEntity(entity: Entity) {
        if (this.entities.indexOf(entity) === -1) {
            this.entities.push(entity);
            this._entityAddedEvent.raise(entity);
        }
    }

    removeEntity(entity: Entity) {
        const index = this.entities.indexOf(entity);
        if (index !== -1) {
            this.entities.splice(index, 1);
            this._entityRemovedEvent.raise(entity);
        }
    }
}

function createQueryKey(types: ComponentId[]): string {
    return types.sort().join('-');
}

type EntityQueryMap = { [key: string]: EntityQuery };

/**
 * Manages all entity queries in the scene. Queries are created on demand
 * and kept up to date throughout the lifetime of the application.
 */
export class EntityQueryManager {
    private readonly _queries: EntityQueryMap = {};
    private readonly _entityManager: EntityManager;

    constructor(entityManager: EntityManager) {
        this._entityManager = entityManager;
    }

    onEntityAdded(entity: Entity) {
        for (const key in entity.components) {
            this.onEntityComponentAdded(entity, entity.components[key]);
        }
    }

    onEntityComponentAdded(entity: Entity, component: Component) {
        for (const name in this._queries) {
            const query = this._queries[name];
            // TODO: Support NOT components
            if (!query.hasComponent(component.typeId) || !query.check(entity)) {
                continue;
            }

            query.addEntity(entity);
        }
    }

    onEntityComponentRemoved(entity: Entity, component: Component) {
        for (const name in this._queries) {
            const query = this._queries[name];
            if (!query.hasComponent(component.typeId)) {
                continue;
            }

            query.removeEntity(entity);
        }
    }

    onEntityRemoved(entity: Entity) {
        for (const name in this._queries) {
            const query = this._queries[name];
            query.removeEntity(entity);
        }
    }

    getQuery(components: ComponentId[]): EntityQuery {
        const key = createQueryKey(components);
        let query = this._queries[key];
        if (!query) {
            query = new EntityQuery(components, key);
            this._queries[key] = query;

            for (const entity of this._entityManager.entities) {
                if (query.check(entity)) {
                    query.addEntity(entity);
                }
            }
        }

        return query;
    }
}
